import { Gender } from "@/types/common";
import api, { AxiosRequestConfigExtra, AxiosResponseWithStatus } from "./axios";
import { ControllerResponse } from "./entities/controllerResponse";
import { currentIANATimeZone } from "@/utils/datetimeHelper";

export interface Member {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  createdOn: string;
  birthday?: string;
  gender: Gender;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  fullName: string;
  age: number;
  mnemonicId?: number;
  memberId: string;
  memberType: string;
  termsOfService: boolean;
  legal?: string[];
  pronouns: string;
  preferredName?: string;
  memberOfferType: string;
  memberOfferPackage: string;
  preferredTimeZoneConfirmed: boolean | undefined | null;
  preferredTimeZone: string | undefined | null;
  preferredIANATimeZone: string;
  externalPartnerId: string | null;
  partnerUserId: string | null;
  identificationVerified: boolean | undefined | null;
  isVerified: boolean | undefined | null;
  verificationType?: string | null;
  optInPhoneNotifications: boolean;
  optInEmailNotifications: boolean;
  functionId: string | null;
}

export enum MemberTaskNames {
  Renewal = "Renewal"
}

export enum TaskUrgency {
  Low = "Low",
  Normal = "Normal",
  High = "High"
}

export interface MemberTask {
  name: MemberTaskNames;
  urgency: TaskUrgency;
  extraData: any;
}

export interface ViewerHappinessScoreDTO {
  rating?: number;
  comment?: string;
  skipped: boolean;
}

export interface CAT {
  createdAt: Date;
  data: any;
}
export interface MemberAssociationDto {
  memberOfferType: string | null;
  memberOfferCode: string | null;
}

export enum BookingStages {
  AccountCreated = "ACCOUNT_CREATED",
  FunctionSignUpPortalEntry = "FUNCTION_SIGNUP_PORTAL_ENTRY",
  SelectScanPage = "SCAN_SELECTION_PAGE",
  PackageSelected = "PACKAGE_SELECTED",
  PackageRejected = "PACKAGE_REJECTED",
  LocationPage = "LOCATION_PAGE",
  CenterSelected = "CENTER_SELECTED",
  PaymentPage = "PAYMENT_PAGE",
  MyMonitorBookNow = "MY_MONITOR_BOOK_NOW"
}

export interface BookingStageDto {
  memberId: string | undefined;
  encounterId?: string;
  stage: BookingStages;
  details?: string;
  visitedOn: Date;
}

export interface AssociationCheckDto {
  hasPartnerAssociation: boolean;
  partnerName: string;
  memberOfferType: string;
}

export class MembersApi {
  async createPendingCharge(
    encounterId: string,
    data: {
      creditAppliedCents: number;
      paymentPlan: string;
      promotionCode: string;
      paymentFor?: string | null;
      isDeferred: boolean;
    }
  ): Promise<string> {
    const response = await api.post<any, AxiosResponseWithStatus<string>>(
      `${import.meta.env.VITE_APP_ENCOUNTERS_URL}/api/payments/${encounterId}/create-pending`,
      data,
      <AxiosRequestConfigExtra>{
        resolveOnError: false
      }
    );
    return response.data;
  }

  async completePendingCharge(encounterId: string): Promise<void> {
    await api.post<any, AxiosResponseWithStatus<ControllerResponse>>(
      `${import.meta.env.VITE_APP_ENCOUNTERS_URL}/api/payments/${encounterId}/complete`,
      {},
      <AxiosRequestConfigExtra>{
        resolveOnError: false
      }
    );
  }

  async addUtm(utm: {
    utmSource: string | (string | null)[] | null | undefined;
    utmMedium: string | (string | null)[] | null | undefined;
    utmCampaign: string | (string | null)[] | null | undefined;
    utmContent: string | (string | null)[] | null | undefined;
    utmGclid: string | (string | null)[] | null | undefined;
    utmTerm: string | (string | null)[] | null | undefined;
  }) {
    await api.put(
      `${import.meta.env.VITE_APP_INDIVIDUALS_URL}/api/members/utm`,
      utm
    );
  }

  /** Creates a user
   * @param {Array<string>} member add legal and checks ToS for the current user
   * @returns {ControllerResponse} The response from the server
   */
  async addToS(legal: Array<string>): Promise<ControllerResponse> {
    const response = await api.put<
      any,
      AxiosResponseWithStatus<ControllerResponse>
    >(`${import.meta.env.VITE_APP_INDIVIDUALS_URL}/api/members/tos`, legal);

    if (!response.success) {
      console.debug("Failed to post tos", response);
    }
    return response.data;
  }

  /** Creates a user
   * @param {object} member The object containing user info.
   * @returns {string} The response from the server
   * This endpoint returns:
   *    201 if the user is created
   *    400 if the user exists
   *    400 if bad request
   */
  async createMember(member: any): Promise<string> {
    const response = await api.post<any, AxiosResponseWithStatus<string>>(
      `${import.meta.env.VITE_APP_INDIVIDUALS_URL}/api/members`,
      member,
      <AxiosRequestConfigExtra>{
        resolveOnError: false
      }
    );

    if (!response.success) {
      console.debug("Failed to post user", response);
    }
    return response.data;
  }

  async createMemberWithMammogramScan(
    member: any,
    appointment: any
  ): Promise<ControllerResponse> {
    const timeZone = currentIANATimeZone();

    const response = await api.post<
      any,
      AxiosResponseWithStatus<ControllerResponse>
    >(
      `${
        import.meta.env.VITE_APP_INDIVIDUALS_URL
      }/api/members/register/mammogram`,
      {
        ...member,
        ...appointment,
        timeZone
      }
    );

    if (!response.success) {
      console.debug("Failed to post user", response);
    }
    return response.data;
  }

  /**
   * Saves CAT for a member
   * @param {object} catData The data to save
   * @returns {ControllerResponse} The response from the server
   * This endpoint returns:
   *    201 if the user is created
   *    404 if the user doesn't exists
   *    400 if bad request
   */
  async saveCat(catData: any): Promise<void> {
    const body = { payload: JSON.stringify(catData) };
    await api.post(
      `${import.meta.env.VITE_APP_INDIVIDUALS_URL}/api/members/cat`,
      body,
      <AxiosRequestConfigExtra>{
        resolveOnError: false
      }
    );
  }

  /**
   * Loads CAT for a member
   * @returns {catData} The CAT data from the server
   * This endpoint returns:
   *    200 if the user and cat exist
   *    404 if the user doesn't exists
   *    400 if bad request / no cat for this user
   */
  async loadCat(): Promise<CAT | undefined> {
    const response = await api.get<any, AxiosResponseWithStatus<any>>(
      `${import.meta.env.VITE_APP_INDIVIDUALS_URL}/api/members/cat`
    );
    if (!response) return undefined;
    const catData = { ...response.data };
    if (catData.payload) catData.data = JSON.parse(catData.payload);
    return catData;
  }

  async loadMemberData(): Promise<Member | undefined> {
    const response = await api.get<any, AxiosResponseWithStatus<Member>>(
      `${import.meta.env.VITE_APP_INDIVIDUALS_URL}/api/members`,
      <AxiosRequestConfigExtra>{ resolveOnError: false }
    );
    if (response.success) {
      return response.data;
    }

    console.debug("Failed to get member", response);
    return undefined;
  }

  async attestIdentification(
    firstName: string,
    lastName: string,
    dateOfBirth: string
  ): Promise<Member> {
    const response = await api.post<any, AxiosResponseWithStatus<Member>>(
      `${import.meta.env.VITE_APP_INDIVIDUALS_URL}/api/members/attest`,
      { firstName, lastName, dateOfBirth },
      <AxiosRequestConfigExtra>{ resolveOnError: false }
    );
    return response.data;
  }

  async updateMember(keyValues: Record<string, any>): Promise<Member> {
    const response = await api.patch<any, AxiosResponseWithStatus<Member>>(
      `${import.meta.env.VITE_APP_INDIVIDUALS_URL}/api/members`,
      keyValues
    );
    return response.data;
  }

  async initialSetup(
    legal: string[],
    email: string,
    newPassword: string,
    passwordResetToken: string,
    optInEmailNotifications: string,
    optInPhoneNotifications: string
  ): Promise<boolean> {
    const timeZone = currentIANATimeZone();
    await api.post<any, AxiosResponseWithStatus<ControllerResponse<boolean>>>(
      `${import.meta.env.VITE_APP_INDIVIDUALS_URL}/api/members/initial-setup`,
      {
        legal,
        email,
        newPassword,
        passwordResetToken,
        timeZone,
        optInEmailNotifications,
        optInPhoneNotifications
      },
      <AxiosRequestConfigExtra>{ resolveOnError: false }
    );
    return true;
  }

  async createViewerHappinessScore(
    happinessScore: ViewerHappinessScoreDTO
  ): Promise<boolean> {
    const response = await api.post<
      ViewerHappinessScoreDTO,
      AxiosResponseWithStatus<ControllerResponse<boolean>>
    >("/api/members/viewerhappinessscore", happinessScore, <
      AxiosRequestConfigExtra
    >{ resolveOnError: false });
    return response.data.result;
  }

  async associate(
    memberAssociationDto: MemberAssociationDto,
    access_token: string
  ): Promise<Member> {
    const response = await api.post<any, AxiosResponseWithStatus<Member>>(
      `${import.meta.env.VITE_APP_INDIVIDUALS_URL}/api/members/associate`,
      memberAssociationDto,
      <AxiosRequestConfigExtra>{
        headers: { Authorization: `Bearer ${access_token || ""}` },
        resolveOnError: false
      }
    );
    return response.data;
  }

  async checkAssociation(access_token: string): Promise<AssociationCheckDto> {
    const response = await api.get<
      any,
      AxiosResponseWithStatus<AssociationCheckDto>
    >(
      `${import.meta.env.VITE_APP_INDIVIDUALS_URL}/api/members/checkassociation`,
      <AxiosRequestConfigExtra>{
        headers: { Authorization: `Bearer ${access_token || ""}` },
        resolveOnError: false
      }
    );

    return response.data;
  }

  async MemberAssociatePartnerInvite(): Promise<Member> {
    const response = await api.post<any, AxiosResponseWithStatus<Member>>(
      `${import.meta.env.VITE_APP_INDIVIDUALS_URL}/api/members/memberassociatepartner`,
      null,
      <AxiosRequestConfigExtra>{
        resolveOnError: false
      }
    );

    return response.data;
  }

  async addBookingStageToJourney(bookingStageDto: BookingStageDto) {
    await api.post<any>(
      `${import.meta.env.VITE_APP_INDIVIDUALS_URL}/api/members/bookingstage`,
      bookingStageDto
    );
    return true;
  }
}

export const membersApi = new MembersApi();
export default membersApi;
